/**
 * EntityPanel — shows all entities of a given type for a project,
 * each with an inline-editable name, scene count, and "Used In" expander.
 *
 * Props:
 *   projectId, token
 *   type          LOCATION | CHARACTER | PROP | COSTUME | VFX | …
 *   entityList    array of entity objects (from BreakdownContext)
 *   onUpdateEntity (entityId, data) → promise
 *   onNavigateScene (sceneId) → void
 *   onDeleteEntity  (entity, action, mergeId?) → promise
 */
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import EditableField from './EditableField';
import { MapPin, User, Package, Shirt, Film, Zap, Music, Truck,
         Rabbit, Sparkles, ChevronDown, ChevronRight, Trash2, AlertTriangle,
         Plus } from 'lucide-react';
import { formatSceneNumber } from '../utils/sceneNumber';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  LOCATION:          { icon: MapPin,    color: '#60A5FA', label: 'Locations' },
  CHARACTER:         { icon: User,      color: '#4ADE80', label: 'Characters' },
  PROP:              { icon: Package,   color: '#FB923C', label: 'Props' },
  COSTUME:           { icon: Shirt,     color: '#F472B6', label: 'Costumes' },
  ART_SET_DRESSING:  { icon: Film,      color: '#C084FC', label: 'Art / Set' },
  VFX:               { icon: Sparkles,  color: '#38BDF8', label: 'VFX' },
  STUNT:             { icon: Zap,       color: '#FBBF24', label: 'Stunts' },
  SFX_SPECIAL_EFFECTS: { icon: Zap,    color: '#F87171', label: 'SFX' },
  VEHICLE:           { icon: Truck,     color: '#94A3B8', label: 'Vehicles' },
  PICTURE_VEHICLE:   { icon: Truck,     color: '#7DD3FC', label: 'Picture Vehicles' },
  ANIMAL:            { icon: Rabbit,    color: '#86EFAC', label: 'Animals' },
  EXTRA_BACKGROUND:  { icon: User,      color: '#D1D5DB', label: 'Extras / BG' },
  MAKEUP_HAIR:       { icon: Sparkles,  color: '#FCA5A5', label: 'Makeup / Hair' },
  SOUND_MUSIC:       { icon: Music,     color: '#A5B4FC', label: 'Sound / Music' },
  PRODUCTION_NOTE:   { icon: Film,      color: '#E5E7EB', label: 'Production Notes' },
};

const DT = {
  bg: 'var(--cb-bg)', card: 'var(--cb-surface)', surface: 'var(--cb-surface-h)',
  border: 'var(--cb-border)', accent: 'var(--cb-accent)',
  textPri: 'var(--cb-text)', textSec: 'var(--cb-text-sec)', textDim: 'var(--cb-text-dim)',
};

/* ── Delete validation dialog ─────────────────────────────────────────── */
function DeleteDialog({ entity, sceneCount, onConfirm, onClose, allEntities }) {
  const [action, setAction] = useState('cancel');
  const [mergeId, setMergeId] = useState('');
  const sameType = allEntities.filter(e => e.type === entity.type && e.id !== entity.id);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6"
        style={{ background: DT.surface, border: `1px solid ${DT.border}`, boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(248,113,113,0.1)' }}>
            <AlertTriangle className="h-5 w-5" style={{ color: '#f87171' }} />
          </div>
          <div>
            <h3 className="font-semibold" style={{ color: DT.textPri }}>Delete &ldquo;{entity.name}&rdquo;?</h3>
            <p className="text-xs" style={{ color: DT.textSec }}>
              Used in <strong style={{ color: '#f87171' }}>{sceneCount} scene{sceneCount !== 1 ? 's' : ''}</strong>
            </p>
          </div>
        </div>

        <div className="space-y-2 mb-5">
          {[
            { id: 'delete_refs', label: 'Delete all references', sub: 'Removes this entity from every scene and item', color: '#f87171' },
            { id: 'merge', label: 'Merge into another entity', sub: 'Reassign all references to another entity', color: '#F5A623' },
            { id: 'cancel', label: 'Cancel', sub: "Don't delete", color: '#a0a0a0' },
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setAction(opt.id)}
              className="w-full rounded-xl p-3 text-left transition-all"
              style={{
                background: action === opt.id ? `${opt.color}18` : 'transparent',
                border: `1px solid ${action === opt.id ? opt.color + '40' : DT.border}`,
              }}
            >
              <span className="text-sm font-medium block" style={{ color: opt.color }}>{opt.label}</span>
              <span className="text-xs" style={{ color: DT.textDim }}>{opt.sub}</span>
            </button>
          ))}
        </div>

        {action === 'merge' && sameType.length > 0 && (
          <select
            value={mergeId}
            onChange={e => setMergeId(e.target.value)}
            className="w-full rounded-lg p-2 mb-4 text-sm"
            style={{ background: DT.bg, border: `1px solid ${DT.border}`, color: DT.textPri }}
          >
            <option value="">Select entity to merge into…</option>
            {sameType.map(e => (
              <option key={e.id} value={e.id}>{e.name} ({e.scene_count || 0} scenes)</option>
            ))}
          </select>
        )}

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-1.5 rounded-lg text-sm" style={{ color: DT.textSec }}>Cancel</button>
          <button
            onClick={() => onConfirm(action, action === 'merge' ? mergeId : undefined)}
            disabled={action === 'cancel' || (action === 'merge' && !mergeId)}
            className="px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-40"
            style={{ background: action === 'merge' ? '#F5A623' : '#f87171', color: '#000' }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Entity Row ─────────────────────────────────────────────────────────── */
function EntityRow({ entity, projectId, token, onUpdate, onNavigateScene, onDeleteRequest, allEntities }) {
  const [expanded, setExpanded] = useState(false);
  const [usage, setUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const meta = TYPE_META[entity.type] || TYPE_META.PROP;

  const loadUsage = async () => {
    if (usage) { setExpanded(e => !e); return; }
    setExpanded(true);
    setLoadingUsage(true);
    try {
      const res = await axios.get(
        `${API}/api/projects/${projectId}/entities/${entity.id}/usage`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsage(res.data);
    } catch { /* silent */ }
    finally { setLoadingUsage(false); }
  };

  return (
    <div
      className="rounded-xl overflow-hidden transition-all group"
      style={{ background: DT.card, border: `1px solid ${DT.border}` }}
      data-testid={`entity-row-${entity.id}`}
    >
      <div className="flex items-center gap-3 p-3">
        {/* Expand toggle */}
        <button
          onClick={loadUsage}
          className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded"
          style={{ color: DT.textDim }}
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>

        {/* Color dot */}
        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: meta.color }} />

        {/* Inline-editable name */}
        <div className="flex-1 min-w-0">
          <EditableField
            value={entity.name}
            onSave={v => onUpdate(entity.id, { name: v })}
            placeholder="Entity name…"
            style={{ color: DT.textPri, fontSize: 13, fontWeight: 500 }}
            data-testid={`entity-name-${entity.id}`}
          />
        </div>

        {/* Scene count badge */}
        <span
          className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
          style={{ background: `${meta.color}14`, color: meta.color }}
        >
          {entity.scene_count ?? 0} scene{(entity.scene_count ?? 0) !== 1 ? 's' : ''}
        </span>

        {/* Notes inline edit */}
        <EditableField
          value={entity.notes}
          onSave={v => onUpdate(entity.id, { notes: v })}
          placeholder="Notes…"
          style={{ color: DT.textSec, fontSize: 11, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          data-testid={`entity-notes-${entity.id}`}
        />

        {/* Delete button */}
        <button
          onClick={() => onDeleteRequest(entity)}
          className="flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ color: DT.textDim }}
          title="Delete entity"
          data-testid={`entity-delete-${entity.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Used-In expander */}
      {expanded && (
        <div className="px-4 pb-3 border-t" style={{ borderColor: DT.border }}>
          {loadingUsage ? (
            <p className="text-xs py-2" style={{ color: DT.textDim }}>Loading scenes…</p>
          ) : usage ? (
            <div className="pt-2">
              <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: DT.textDim }}>
                Used In ({(() => {
                  const seen = new Set();
                  return (usage.scenes || []).filter(s => {
                    const k = s.id || s._id;
                    if (!k || seen.has(k)) return false;
                    seen.add(k);
                    return true;
                  }).length;
                })()} scenes)
              </p>
              <div className="space-y-1">
                {(() => {
                  // BUG FIX: dedupe by scene id — backend may emit the same
                  // scene twice when an entity (location/character) is
                  // referenced via more than one tagging path. Group
                  // semantics unchanged (scene still appears under every
                  // matching location/character), just no duplicate row
                  // inside a single entity's expander.
                  const seen = new Set();
                  return (usage.scenes || []).filter(s => {
                    const k = s.id || s._id;
                    if (!k || seen.has(k)) return false;
                    seen.add(k);
                    return true;
                  });
                })().map(s => (
                  <button
                    key={s.id}
                    onClick={() => onNavigateScene && onNavigateScene(s.id)}
                    className="w-full flex items-start gap-2 px-2 py-1.5 rounded-lg text-left"
                    style={{ background: 'transparent' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    data-testid={`entity-scene-link-${s.scene_number}`}
                  >
                    <span className="flex-shrink-0 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded mt-0.5"
                          style={{ background: `${meta.color}18`, color: meta.color }}>
                      {formatSceneNumber(s.scene_number, { padStart: 2 })}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="text-[11px] block truncate" style={{ color: DT.textSec }}>
                        {s.scene_header || '—'}
                      </span>
                      {s.synopsis && (
                        <span className="text-[10px] block truncate mt-0.5" style={{ color: DT.textDim }}>
                          {s.synopsis}
                        </span>
                      )}
                    </span>
                    <span className="text-[9px] flex-shrink-0 mt-0.5" style={{ color: DT.textDim }}>
                      {s.time_of_day || ''}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs py-2" style={{ color: DT.textDim }}>No scene usage found.</p>)}
        </div>
      )}
    </div>
  );
}

/* ── Main EntityPanel ───────────────────────────────────────────────────── */
export function EntityPanel({ projectId, token, type, entityList = [], onUpdateEntity,
                               onNavigateScene, onRunMigration }) {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSceneCount, setDeleteSceneCount] = useState(0);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [migrating, setMigrating] = useState(false);

  // Auto-sync on first mount so entities are always populated
  React.useEffect(() => {
    if (filtered.length === 0) {
      onRunMigration?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const meta = TYPE_META[type] || TYPE_META.PROP;
  const Icon = meta.icon;
  // Sort by scene_count desc (most used first), dedup by name (case-insensitive)
  const seenNames = new Set();
  const filtered = entityList
    .filter(e => e.type === type)
    .sort((a, b) => (b.scene_count ?? 0) - (a.scene_count ?? 0))
    .filter(e => {
      const key = (e.name || '').trim().toLowerCase();
      if (seenNames.has(key)) return false;
      seenNames.add(key);
      return true;
    });

  const handleDeleteRequest = useCallback(async (entity) => {
    setDeleteTarget(entity);
    try {
      const res = await axios.delete(
        `${API}/api/projects/${projectId}/entities/${entity.id}?action=check`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDeleteSceneCount(res.data.scene_count || 0);
    } catch { setDeleteSceneCount(0); }
  }, [projectId, token]);

  const handleDeleteConfirm = useCallback(async (action, mergeId) => {
    if (!deleteTarget) return;
    const params = new URLSearchParams({ action });
    if (mergeId) params.set('merge_into_id', mergeId);
    await axios.delete(
      `${API}/api/projects/${projectId}/entities/${deleteTarget.id}?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setDeleteTarget(null);
    // Trigger entity refresh via parent
    onUpdateEntity && await onUpdateEntity(deleteTarget.id, { _deleted: true });
  }, [deleteTarget, projectId, token, onUpdateEntity]);

  const handleCreate = async () => {
    const n = newName.trim();
    if (!n) return;
    setCreating(true);
    try {
      await axios.post(
        `${API}/api/projects/${projectId}/entities`,
        { type, name: n },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewName('');
      onUpdateEntity && await onUpdateEntity('__refresh__', {});
    } catch { /* silent */ }
    finally { setCreating(false); }
  };

  const handleMigrate = async () => {
    setMigrating(true);
    try {
      await onRunMigration?.();
    } finally { setMigrating(false); }
  };

  return (
    <div className="space-y-3" data-testid={`entity-panel-${type}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4" style={{ color: meta.color }} />
          <h3 className="text-sm font-semibold" style={{ color: DT.textPri }}>{meta.label}</h3>
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${meta.color}14`, color: meta.color }}>
            {filtered.length}
          </span>
        </div>
        <button
          onClick={handleMigrate}
          disabled={migrating}
          className="text-[10px] px-2.5 py-1 rounded-lg disabled:opacity-50"
          style={{ background: 'rgba(255,255,255,0.05)', color: DT.textDim, border: `1px solid ${DT.border}` }}
          title="Sync entities from scene data"
        >
          {migrating ? 'Syncing…' : '↺ Sync'}
        </button>
      </div>

      {/* Add new entity */}
      <div className="flex gap-2 mb-3">
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
          placeholder={`Add new ${(meta.label || 'entity').replace(/s$/, '').toLowerCase()}…`}
          className="flex-1 rounded-lg px-3 py-1.5 text-sm"
          style={{ background: DT.bg, border: `1px solid ${DT.border}`, color: DT.textPri, outline: 'none' }}
          data-testid={`add-entity-input-${type}`}
        />
        <button
          onClick={handleCreate}
          disabled={!newName.trim() || creating}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-40 flex items-center gap-1"
          style={{ background: meta.color, color: '#000' }}
          data-testid={`add-entity-btn-${type}`}
        >
          <Plus className="h-3 w-3" />Add
        </button>
      </div>

      {/* Entity rows */}
      {filtered.length === 0 ? (
        <div className="text-center py-8" style={{ color: DT.textDim }}>
          <p className="text-xs">No {meta.label.toLowerCase()} found yet.</p>
          <p className="text-[10px] mt-1">Syncing from scene data automatically…</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {filtered.map(entity => (
            <EntityRow
              key={entity.id}
              entity={entity}
              projectId={projectId}
              token={token}
              onUpdate={onUpdateEntity}
              onNavigateScene={onNavigateScene}
              onDeleteRequest={handleDeleteRequest}
              allEntities={filtered}
            />
          ))}
        </div>
      )}

      {/* Delete dialog */}
      {deleteTarget && (
        <DeleteDialog
          entity={deleteTarget}
          sceneCount={deleteSceneCount}
          allEntities={filtered}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

export default EntityPanel;
